import {
  FeatureName,
  OrderByInput,
  useIsFeatureEnabled,
  useRoutineListOrder,
  useSetRoutineListOrder,
} from '@gripp/shared-logic';
import { InfiniteDataGrid, SelectionStatus } from '@gripp/shared-ui';
import { ColumnDef } from '@tanstack/react-table';
import { StyleSheet, View } from 'react-native';
import { useColumnsRoutine } from './useColumnsRoutine';

type RoutineListProps = {
  filter: any;
  paddingBottom?: number;
  updateRowsSelected?: (rows: Array<any>) => void;
  showCheckboxColumn: boolean;
  showImpersonateWorkspace: boolean;
  onItemsCountChange?: (count: number) => void;
  headerContentComponent?: React.ReactNode;
  assetsButton: (data: any) => React.ReactNode;
  addonsButton?: (data: any) => React.ReactNode;
  groupsButton?: (data: {
    groups: any[];
    entityId: string;
    name: string;
  }) => React.ReactNode;
  onRowClick?: (row: any) => void;
  isProducerWorkspace?: boolean;
  isRowSelected?: (row: any) => boolean;
  selectionStatus?: SelectionStatus;
  onSelectionStatusChange?: (status: SelectionStatus) => void;
};

export const RoutineList = ({
  filter,
  paddingBottom = 10,
  updateRowsSelected,
  showCheckboxColumn,
  headerContentComponent,
  assetsButton,
  addonsButton,
  groupsButton,
  onRowClick,
  isProducerWorkspace = false,
  isRowSelected,
  selectionStatus,
  onSelectionStatusChange,
  onItemsCountChange,
}: RoutineListProps) => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  const { data: routineOrder } = useRoutineListOrder();
  const { mutateAsync: setRoutineOrder } = useSetRoutineListOrder();

  const columns = useColumnsRoutine(
    showCheckboxColumn,
    assetsButton,
    isProducerWorkspace,
    addonsButton,
    groupsButton
  ) as ColumnDef<any, any>[];

  const onOrderByChange = async (orderBy: OrderByInput) => {
    await setRoutineOrder(orderBy);
  };

  return (
    <View
      style={[
        styles.routinesContainer,
        isCropCircleEnabled && styles.routinesContainerCropCircle,
      ]}
    >
      <InfiniteDataGrid
        columns={columns}
        paddingBottom={paddingBottom}
        query={LIST_ROUTINES}
        dataPrefix="routine.listRoutines"
        modelName="routine"
        orderBy={routineOrder}
        pageSize={50}
        filter={filter}
        hideHeader={false}
        showScrollIndicator={true}
        updateRowsSelected={updateRowsSelected}
        isRowSelected={isRowSelected}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={onSelectionStatusChange}
        onOrderByChange={onOrderByChange}
        headerContent={headerContentComponent}
        onRowClick={onRowClick}
        onItemsCountChange={onItemsCountChange}
      />
    </View>
  );
};

const LIST_ROUTINES = `
  query FindRoutines($filter: ListRoutinesFilter, $page: PageRequest, $orderBy: OrderByInput) {
    routine {
      listRoutines(filter: $filter, page: $page, orderBy: $orderBy) {
        count
        limit
        offset
        items {
          id
          name
          type
          config
          isWorkspaceRoutine
          assetsCount
          allAssetsCount
          addonsCount
          submissionsCount
          groups {
            id
            name
          }
          workspace {
            id
            name
            type
          }
          lastSubmitted
          createdAt
          updatedAt
        }
      }
    }
  }
`;

const styles = StyleSheet.create({
  routinesContainer: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: -1,
  },
  routinesContainerCropCircle: {
    borderRadius: 0,
  },
});
